import axios from 'axios';
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Calculator, Plus, Edit, Trash2, Save, X, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

const TYPE_MESURE_LABELS = {
  pi2: { label: 'Pi² (superficie)', unit: 'pi²', placeholder: '1 200' },
  lineaire: { label: 'Pied linéaire', unit: 'pi.lin.', placeholder: '150' },
  heures: { label: 'Heures estimées', unit: 'h', placeholder: '24' },
  unite: { label: 'Unité (portes, fenêtres...)', unit: 'unité', placeholder: '12' },
  fixe: { label: 'Prix fixe (aucune mesure)', unit: '—', placeholder: '—' },
};

const EMPTY_REGLE = {
  nom: '',
  description: '',
  type_mesure: 'pi2',
  unite_label: '',
  // Prix par unité pour chaque tier
  prix_tres_bon: '',
  prix_bon: '',
  prix_correct: '',
  // Frais fixes ajoutés au total
  frais_fixes_tres_bon: 0,
  frais_fixes_bon: 0,
  frais_fixes_correct: 0,
  actif: true,
};

export const ConfigurationReglesCalcul = () => {
  const [regles, setRegles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRegle, setEditingRegle] = useState(null);
  const [form, setForm] = useState(EMPTY_REGLE);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => { loadRegles(); }, []);

  const loadRegles = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/calculatrice/regles`);
      const d = r.data;
      setRegles(d.regles || []);
    } catch (e) { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditingRegle(null);
    setForm(EMPTY_REGLE);
    setShowModal(true);
  };

  const openEdit = (regle) => {
    setEditingRegle(regle);
    setForm({ ...EMPTY_REGLE, ...regle });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.nom.trim()) { toast.error('Le nom est requis'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        prix_tres_bon: parseFloat(form.prix_tres_bon) || 0,
        prix_bon: parseFloat(form.prix_bon) || 0,
        prix_correct: parseFloat(form.prix_correct) || 0,
        frais_fixes_tres_bon: parseFloat(form.frais_fixes_tres_bon) || 0,
        frais_fixes_bon: parseFloat(form.frais_fixes_bon) || 0,
        frais_fixes_correct: parseFloat(form.frais_fixes_correct) || 0,
      };
      const r = editingRegle
        ? await axios.put(`${API_URL}/api/calculatrice/regles/${editingRegle.id}`, payload)
        : await axios.post(`${API_URL}/api/calculatrice/regles`, payload);
      if (r.status !== 200) throw new Error();
      toast.success(editingRegle ? 'Règle mise à jour' : 'Règle créée');
      setShowModal(false);
      loadRegles();
    } catch { toast.error('Erreur sauvegarde'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette règle ?')) return;
    await axios.delete(`${API_URL}/api/calculatrice/regles/${id}`);
    toast.success('Règle supprimée');
    loadRegles();
  };

  const toggleActive = async (regle) => {
    await axios.put(`${API_URL}/api/calculatrice/regles/${regle.id}`, { ...regle, actif: !regle.actif });
    loadRegles();
  };

  const fmt = (v) => v?.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 2 }) ?? '—';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Calculator className="w-8 h-8 text-emerald-600" />
            Règles de Calcul
          </h1>
          <p className="text-slate-500 mt-1">
            Définissez vos catégories de travaux et leurs prix (Très bon / Bon / Correct)
          </p>
        </div>
        <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700 flex items-center gap-2">
          <Plus className="w-4 h-4" /> Nouvelle règle
        </Button>
      </div>

      {/* Légende */}
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="pt-4 pb-3">
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="flex items-center gap-2 p-2 bg-emerald-50 rounded-lg border border-emerald-200">
              <span className="text-lg">⭐⭐⭐</span>
              <div><p className="font-semibold text-emerald-800">Très bon</p><p className="text-xs text-emerald-600">Prix premium — client idéal</p></div>
            </div>
            <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg border border-blue-200">
              <span className="text-lg">⭐⭐</span>
              <div><p className="font-semibold text-blue-800">Bon</p><p className="text-xs text-blue-600">Prix standard — objectif normal</p></div>
            </div>
            <div className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg border border-amber-200">
              <span className="text-lg">⭐</span>
              <div><p className="font-semibold text-amber-800">Correct</p><p className="text-xs text-amber-600">Prix plancher — minimum acceptable</p></div>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" /></div>
      ) : regles.length === 0 ? (
        <Card className="border-dashed border-2 border-slate-300">
          <CardContent className="py-12 text-center">
            <Calculator className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">Aucune règle de calcul</p>
            <p className="text-slate-400 text-sm mt-1">Créez votre première règle pour commencer à utiliser la calculatrice dans vos dossiers.</p>
            <Button onClick={openCreate} className="mt-4 bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4 mr-2" /> Créer ma première règle
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {regles.map(regle => {
            const tm = TYPE_MESURE_LABELS[regle.type_mesure] || {};
            const isExpanded = expanded === regle.id;
            return (
              <Card key={regle.id} className={`transition-all ${!regle.actif ? 'opacity-50' : ''}`}>
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <button onClick={() => setExpanded(isExpanded ? null : regle.id)} className="text-slate-400 hover:text-slate-600">
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-slate-900">{regle.nom}</span>
                          <Badge variant="outline" className="text-xs">{tm.label || regle.type_mesure}</Badge>
                          {!regle.actif && <Badge variant="secondary" className="text-xs">Inactif</Badge>}
                        </div>
                        {regle.description && <p className="text-xs text-slate-500 mt-0.5">{regle.description}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {/* Prix résumé */}
                      <div className="hidden md:flex items-center gap-4 text-sm mr-4">
                        <span className="text-emerald-600 font-semibold">{regle.type_mesure === 'fixe' ? fmt(regle.prix_tres_bon) : `${fmt(regle.prix_tres_bon)}/${tm.unit}`}</span>
                        <span className="text-blue-600">{regle.type_mesure === 'fixe' ? fmt(regle.prix_bon) : `${fmt(regle.prix_bon)}/${tm.unit}`}</span>
                        <span className="text-amber-600">{regle.type_mesure === 'fixe' ? fmt(regle.prix_correct) : `${fmt(regle.prix_correct)}/${tm.unit}`}</span>
                      </div>
                      <button onClick={() => toggleActive(regle)} className={`text-xs px-2 py-1 rounded border transition-all ${regle.actif ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50' : 'border-gray-300 text-gray-500 hover:bg-gray-50'}`}>
                        {regle.actif ? 'Actif' : 'Inactif'}
                      </button>
                      <Button size="sm" variant="outline" onClick={() => openEdit(regle)}><Edit className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(regle.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t grid grid-cols-3 gap-4">
                      {[
                        { key: 'tres_bon', label: '⭐⭐⭐ Très bon', color: 'emerald' },
                        { key: 'bon', label: '⭐⭐ Bon', color: 'blue' },
                        { key: 'correct', label: '⭐ Correct', color: 'amber' },
                      ].map(tier => (
                        <div key={tier.key} className={`p-3 rounded-lg bg-${tier.color}-50 border border-${tier.color}-200`}>
                          <p className={`text-xs font-semibold text-${tier.color}-700 mb-2`}>{tier.label}</p>
                          {regle.type_mesure !== 'fixe' && (
                            <p className="text-sm text-slate-700">
                              Taux : <strong>{fmt(regle[`prix_${tier.key}`])}</strong> / {tm.unit}
                            </p>
                          )}
                          {regle[`frais_fixes_${tier.key}`] > 0 && (
                            <p className="text-sm text-slate-700">
                              + Frais fixes : <strong>{fmt(regle[`frais_fixes_${tier.key}`])}</strong>
                            </p>
                          )}
                          {regle.type_mesure === 'fixe' && (
                            <p className="text-sm text-slate-700">Prix : <strong>{fmt(regle[`prix_${tier.key}`])}</strong></p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal Création/Édition */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRegle ? 'Modifier la règle' : 'Nouvelle règle de calcul'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            {/* Identification */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Nom de la catégorie <span className="text-red-500">*</span></Label>
                <Input
                  placeholder="Ex: Revêtement extérieur Maibec, Peinture intérieure..."
                  value={form.nom}
                  onChange={e => setForm({ ...form, nom: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div className="col-span-2">
                <Label>Description (optionnel)</Label>
                <Input
                  placeholder="Ex: Pose de planches Maibec sur façade, 2 couches primaire + 1 finition"
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Type de mesure</Label>
                <Select value={form.type_mesure} onValueChange={v => setForm({ ...form, type_mesure: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_MESURE_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {form.type_mesure === 'unite' && (
                <div>
                  <Label>Nom de l'unité</Label>
                  <Input
                    placeholder="Ex: porte, fenêtre, section..."
                    value={form.unite_label}
                    onChange={e => setForm({ ...form, unite_label: e.target.value })}
                    className="mt-1"
                  />
                </div>
              )}
            </div>

            <div className="border-t pt-4">
              <p className="text-sm font-semibold text-slate-700 mb-3">
                {form.type_mesure === 'fixe' ? 'Prix fixe par tier' : `Prix par ${TYPE_MESURE_LABELS[form.type_mesure]?.unit || 'unité'} + frais fixes`}
              </p>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { key: 'tres_bon', label: '⭐⭐⭐ Très bon', color: 'emerald' },
                  { key: 'bon', label: '⭐⭐ Bon', color: 'blue' },
                  { key: 'correct', label: '⭐ Correct', color: 'amber' },
                ].map(tier => (
                  <div key={tier.key} className={`p-3 rounded-lg bg-${tier.color}-50 border border-${tier.color}-200 space-y-3`}>
                    <p className={`text-xs font-semibold text-${tier.color}-700`}>{tier.label}</p>
                    <div>
                      <Label className="text-xs">
                        {form.type_mesure === 'fixe' ? 'Prix ($)' : `Taux par ${TYPE_MESURE_LABELS[form.type_mesure]?.unit || 'unité'} ($)`}
                      </Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={form[`prix_${tier.key}`]}
                        onChange={e => setForm({ ...form, [`prix_${tier.key}`]: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    {form.type_mesure !== 'fixe' && (
                      <div>
                        <Label className="text-xs">Frais fixes ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          value={form[`frais_fixes_${tier.key}`]}
                          onChange={e => setForm({ ...form, [`frais_fixes_${tier.key}`]: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Aperçu rapide */}
            {form.prix_bon > 0 && form.type_mesure !== 'fixe' && (
              <div className="bg-slate-50 rounded-lg p-3 border">
                <p className="text-xs font-semibold text-slate-600 mb-2">Aperçu — pour 100 {TYPE_MESURE_LABELS[form.type_mesure]?.unit}</p>
                <div className="flex gap-6 text-sm">
                  {[
                    { key: 'tres_bon', label: '⭐⭐⭐', color: 'text-emerald-600' },
                    { key: 'bon', label: '⭐⭐', color: 'text-blue-600' },
                    { key: 'correct', label: '⭐', color: 'text-amber-600' },
                  ].map(t => {
                    const total = (parseFloat(form[`prix_${t.key}`]) || 0) * 100 + (parseFloat(form[`frais_fixes_${t.key}`]) || 0);
                    return <span key={t.key} className={`font-semibold ${t.color}`}>{t.label} {total.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })}</span>;
                  })}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Annuler</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
              {saving ? 'Sauvegarde...' : <><Save className="w-4 h-4 mr-2" />Enregistrer</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConfigurationReglesCalcul;
